import {Component} from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import FormLabel from '@mui/material/FormLabel'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

// Dispositivo SAS ( IGT Slot Accounting System 6.02 ). Una sola placa SMIB ( ESP32 + Ethernet ) con dos modos:
//   EGM : va dentro del cajero; el cajero responde como maquina ante la SMIB de cualquier proveedor.
//   HOST: va en una maquina de juego; interroga la maquina y reporta a nuestro servidor.
// El cajero lee de esta seccion "tipo", "puerto", "velocidad" y "estado" ( igual que los demas equipos );
// el resto de campos los pasa a la placa con SAS|CONFIG. Diseno: cajero-casino/doc/SAS_DISENO.md

const SI_NO = [{nombre: "Si", valor: "SI"}, {nombre: "No", valor: "NO"}]
const ACTIVADO = [{nombre: "Activado", valor: "SI"}, {nombre: "Desactivado", valor: "NO"}]

// Valores por defecto: los de una SMIB estandar con validacion de sistema y tickets habilitados
const DEFAULTS = {
    tipo: "sas",
    // Protocolo SAS
    modo: "EGM",
    direccion: "1",
    versionSas: "6.02",
    velocidadSas: "19200",
    canalHost: "PRIMARIO",
    pollMs: "200",
    numeroActivo: "",
    numeroSerie: "",
    denominacionContable: "1.00",
    validacion: "SISTEMA",
    redencionTickets: "SI",
    emisionTickets: "SI",
    ticketsRestringidos: "NO",
    montoMaxTicket: "",
    diasVencimiento: "30",
    aft: "NO",
    aftLimite: "",
    excGeneral: "SI",
    excEstado: "SI",
    excSeguridad: "SI",
    excError: "SI",
    eventosTiempoReal: "NO",
    resetRemotoHandpay: "NO",
    reenviarHandpay: "NO",
    // Configuracion SMIB
    modeloSmib: "SMIB-ESP32",
    conexion: "USB",
    puerto: "/dev/sas",
    velocidad: "115200",
    redModo: "DHCP",
    ip: "",
    mascara: "",
    gateway: "",
    dns: "",
    servidorUrl: "",
    contadoresS: "60",
    debug: "1",
    estado: "A"
}

// Campos del formulario por grupo. tipo: select | text | number. ayuda: texto bajo el campo.
const GRUPOS_SAS = [
    {titulo: "Identificación y enlace", campos: [
        {key: "modo", label: "Modo", tipo: "select", opciones: [
            {nombre: "EGM - el cajero responde como máquina", valor: "EGM"},
            {nombre: "HOST - la SMIB interroga la máquina", valor: "HOST"}]},
        {key: "direccion", label: "Dirección SAS (1-127)", tipo: "number", min: 1, max: 127},
        {key: "versionSas", label: "Versión SAS", tipo: "select", opciones: ["6.02", "6.03"]},
        {key: "velocidadSas", label: "Velocidad SAS", tipo: "select", opciones: ["19200"], ayuda: "Fija por norma: 8 bits + bit de wakeup"},
        {key: "canalHost", label: "Canal / control de máquina", tipo: "select", opciones: [
            {nombre: "SAS Primario", valor: "PRIMARIO"}, {nombre: "SAS Secundario", valor: "SECUNDARIO"}]},
        {key: "pollMs", label: "Intervalo de poll (ms)", tipo: "select", opciones: ["40", "100", "200"], ayuda: "Tickets y eventos en tiempo real requieren 40 ms"},
        {key: "numeroActivo", label: "Número de activo (asset)", tipo: "number", min: 0, ayuda: "Requerido para AFT"},
        {key: "numeroSerie", label: "Número de serie", tipo: "text"},
        {key: "denominacionContable", label: "Denominación contable", tipo: "select",
            opciones: ["0.01", "0.05", "0.10", "0.25", "0.50", "1.00", "5.00", "10.00", "20.00", "100.00"],
            ayuda: "Valor de un crédito en los contadores"}
    ]},
    {titulo: "Validación y tickets (TITO)", campos: [
        {key: "validacion", label: "Validación", tipo: "select", opciones: [
            {nombre: "Sistema (el host asigna el número)", valor: "SISTEMA"},
            {nombre: "Secure Enhanced (la máquina genera)", valor: "SECURE_ENHANCED"},
            {nombre: "Standard (sin redención automática)", valor: "STANDARD"}]},
        {key: "redencionTickets", label: "Entrada de tickets (redención)", tipo: "select", opciones: SI_NO},
        {key: "emisionTickets", label: "Salida de tickets (emisión)", tipo: "select", opciones: SI_NO},
        {key: "ticketsRestringidos", label: "Tickets restringidos", tipo: "select", opciones: SI_NO},
        {key: "montoMaxTicket", label: "Monto máximo por ticket", tipo: "number", min: 0, ayuda: "Vacío = sin límite"},
        {key: "diasVencimiento", label: "Días de vencimiento", tipo: "number", min: 0, max: 9999}
    ]},
    {titulo: "Transferencias AFT (cashless)", campos: [
        {key: "aft", label: "AFT", tipo: "select", opciones: [{nombre: "Habilitado", valor: "SI"}, {nombre: "Inhibido", valor: "NO"}]},
        {key: "aftLimite", label: "Límite por transferencia", tipo: "number", min: 0, ayuda: "Vacío = sin límite"}
    ]},
    {titulo: "Excepciones y eventos", campos: [
        {key: "excGeneral", label: "Excepción general", tipo: "select", opciones: ACTIVADO},
        {key: "excEstado", label: "Excepción de status", tipo: "select", opciones: ACTIVADO},
        {key: "excSeguridad", label: "Excepción de seguridad", tipo: "select", opciones: ACTIVADO},
        {key: "excError", label: "Excepción de error", tipo: "select", opciones: ACTIVADO},
        {key: "eventosTiempoReal", label: "Eventos en tiempo real (RTE)", tipo: "select", opciones: SI_NO}
    ]},
    {titulo: "Pagos manuales (handpay)", campos: [
        {key: "resetRemotoHandpay", label: "Reseteo remoto de pagos manuales", tipo: "select", opciones: SI_NO},
        {key: "reenviarHandpay", label: "Reenviar pago manual pendiente", tipo: "select", opciones: SI_NO}
    ]}
]

const GRUPOS_SMIB = [
    {titulo: "Conexión de la placa", campos: [
        {key: "modeloSmib", label: "Modelo SMIB", tipo: "select", opciones: [{nombre: "SMIB ESP32 + Ethernet (propia)", valor: "SMIB-ESP32"}]},
        {key: "conexion", label: "Conexión con MoneyManager", tipo: "select", opciones: [
            {nombre: "USB", valor: "USB"}, {nombre: "Serial DB9", valor: "SERIAL"}, {nombre: "Ethernet TCP", valor: "ETHERNET"}],
            ayuda: "Solo en modo EGM"},
        {key: "puerto", label: "Puerto", tipo: "text", ayuda: "/dev/sas, /dev/ttyS0 o IP de la placa"},
        {key: "velocidad", label: "Velocidad / puerto TCP", tipo: "text", ayuda: "115200 en USB/serial; puerto TCP en Ethernet"},
        {key: "debug", label: "Debug", tipo: "select", opciones: ["1", "2", "3", "4"]},
        {key: "estado", label: "Estado", tipo: "select", opciones: [{nombre: "Activo", valor: "A"}, {nombre: "Inactivo", valor: "I"}]}
    ]},
    {titulo: "Red de la placa (Ethernet)", campos: [
        {key: "redModo", label: "Dirección IP", tipo: "select", opciones: [{nombre: "DHCP", valor: "DHCP"}, {nombre: "Estática", valor: "ESTATICA"}]},
        {key: "ip", label: "IP", tipo: "text"},
        {key: "mascara", label: "Máscara", tipo: "text"},
        {key: "gateway", label: "Gateway", tipo: "text"},
        {key: "dns", label: "DNS", tipo: "text"}
    ]},
    {titulo: "Servidor (modo HOST)", campos: [
        {key: "servidorUrl", label: "URL del servidor", tipo: "text", ayuda: "Backend de dispositivos ( casino/ticket )", xs: 8},
        {key: "contadoresS", label: "Envío de contadores (s)", tipo: "number", min: 10}
    ]}
]

const RE_IP = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/

function Row(props) {
    var item = props.item
    return(
        <TableRow>
            <TableCell>{item.modo}</TableCell>
            <TableCell>{item.direccion}</TableCell>
            <TableCell>{item.validacion}</TableCell>
            <TableCell>{item.conexion}</TableCell>
            <TableCell>{item.puerto}</TableCell>
            <TableCell>{item.estado}</TableCell>
            <TableCell>
                <Button variant="contained" size='small' fullWidth onClick={() => {props.eventSelect(item)}}>Seleccionar</Button>
                <Button variant="contained" size='small' color="error" fullWidth onClick={() => {props.eventDelete(item)}}>Borrar</Button>
            </TableCell>
        </TableRow>
    )
}

class TabSas extends Component {
    constructor(props) {
        super(props)

        this.guardarEvent = props.event

        this.state = {
            tipoDispositivo: props.tipoDispositivo,
            dispositivos: props.dispositivos,
            itemEdicion: null,
            cfg: Object.assign({}, DEFAULTS),
            modoEdicion: false
        }
    }

    onChangeCampo = (key, value) => {
        var cfg = Object.assign({}, this.state.cfg)
        cfg[key] = value === undefined || value === null ? "" : value.toString()
        this.setState({cfg: cfg})
    }

    btnSeleccionarClick = (item) => {
        this.setState({
            itemEdicion: item,
            cfg: Object.assign({}, DEFAULTS, item),
            modoEdicion: true
        })
    }

    btnBorrarClick = (item) => {
        if(window.confirm("¿Borrar dispositivo?")) {
            this.prepararNuevo()
            var dispositivos = this.state.dispositivos
            var x = dispositivos.indexOf(item)
            if(x >= 0) dispositivos.splice(x, 1)
            this.setState({dispositivos: dispositivos})
            this.guardarEvent(this.state.tipoDispositivo, this.state.dispositivos, this.confirmacionGuardado)
        }
    }

    // Reglas minimas para que la configuracion sea valida en cualquier SMIB
    validar = (cfg) => {
        var dir = parseInt(cfg.direccion, 10)
        if(isNaN(dir) || dir < 1 || dir > 127) return "La dirección SAS debe estar entre 1 y 127."
        if(cfg.modo === "EGM" && cfg.puerto.trim() === "") return "El puerto es obligatorio en modo EGM."
        if(cfg.modo === "EGM" && cfg.conexion === "ETHERNET" && !RE_IP.test(cfg.puerto.trim())) return "Con conexión Ethernet el puerto debe ser la IP de la placa."
        if(cfg.redModo === "ESTATICA" && (!RE_IP.test(cfg.ip.trim()) || !RE_IP.test(cfg.mascara.trim()))) return "Con IP estática indique IP y máscara válidas."
        if(cfg.modo === "HOST" && cfg.servidorUrl.trim() === "") return "En modo HOST indique la URL del servidor."
        if(cfg.aft === "SI" && cfg.numeroActivo.trim() === "") return "AFT requiere el número de activo."
        if(cfg.validacion === "STANDARD" && cfg.redencionTickets === "SI")
            return "La validación Standard no permite la redención automática de tickets: use Sistema o Secure Enhanced."
        return null
    }

    btnGuardarClick = () => {
        var cfg = this.state.cfg
        var error = this.validar(cfg)
        if(error) {
            window.alert(error)
            return
        }

        var dispositivos = this.state.dispositivos
        var item = Object.assign({}, cfg)

        if(this.state.modoEdicion) {
            var x = dispositivos.indexOf(this.state.itemEdicion)
            if(x >= 0) dispositivos[x] = item
        } else {
            dispositivos.push(item)
        }

        this.setState({dispositivos: dispositivos})
        this.guardarEvent(this.state.tipoDispositivo, this.state.dispositivos, this.confirmacionGuardado)
    }

    prepararNuevo = () => {
        this.setState({
            itemEdicion: null,
            cfg: Object.assign({}, DEFAULTS),
            modoEdicion: false
        })
    }

    confirmacionGuardado = () => {
        this.prepararNuevo()
    }

    renderCampo(campo) {
        var valor = this.state.cfg[campo.key]
        var xs = campo.xs || 4

        if(campo.tipo === "select") {
            return(
                <Grid item xs={xs} key={campo.key}>
                    <FormControl fullWidth component="fieldset">
                        <InputLabel>{campo.label}</InputLabel>
                        <Select
                            label={campo.label}
                            value={valor}
                            onChange={(e) => this.onChangeCampo(campo.key, e.target.value)}>
                            {campo.opciones.map(op => {
                                var o = typeof op === 'string' ? {nombre: op, valor: op} : op
                                return(<MenuItem key={o.valor} value={o.valor}>{o.nombre}</MenuItem>)
                            })}
                        </Select>
                        {campo.ayuda ? <Typography variant="caption" color="text.secondary">{campo.ayuda}</Typography> : null}
                    </FormControl>
                </Grid>
            )
        }

        return(
            <Grid item xs={xs} key={campo.key}>
                <FormControl fullWidth component="fieldset">
                    <TextField
                        label={campo.label}
                        variant="outlined"
                        type={campo.tipo === "number" ? "number" : "text"}
                        inputProps={campo.tipo === "number" ? {min: campo.min, max: campo.max} : {}}
                        value={valor}
                        onChange={(e) => this.onChangeCampo(campo.key, e.target.value)}
                        helperText={campo.ayuda}
                        fullWidth
                    />
                </FormControl>
            </Grid>
        )
    }

    renderGrupos(grupos) {
        return grupos.map(grupo => (
            <Box key={grupo.titulo} sx={{mt: 2}}>
                <Typography variant="subtitle2" sx={{mb: 1.5}}>{grupo.titulo}</Typography>
                <Grid container spacing={2}>
                    {grupo.campos.map(campo => this.renderCampo(campo))}
                </Grid>
            </Box>
        ))
    }

    render() {
        const columns = [
            {field: 'Modo', headerName: 'Modo', width: 70},
            {field: 'Direccion', headerName: 'Dirección', width: 70},
            {field: 'Validacion', headerName: 'Validación', width: 110},
            {field: 'Conexion', headerName: 'Conexión', width: 90},
            {field: 'Puerto', headerName: 'Puerto', width: 130},
            {field: 'Estado', headerName: 'Estado', width: 60},
            {field: '', headerName: '', width: 120}
        ]

        return(
            <Box>
                <Grid container xs={12}>
                    <Grid item xs={12}>
                        <Card fullWidth>
                            <CardContent>
                                <h3>Dispositivo SAS</h3>
                                <form>
                                    <FormLabel>Protocolo SAS (Slot Accounting System)</FormLabel>
                                    <Box sx={{fontSize: 13, color: 'text.secondary', mt: 1}}>
                                        Estándar IGT SAS 6.02: enlace RS-232 a 19200 baudios con bit de wakeup, respuesta
                                        en menos de 20 ms. En modo <b>EGM</b> el cajero responde como una máquina ante la
                                        SMIB de cualquier proveedor; en modo <b>HOST</b> la placa interroga la máquina de
                                        juego. Estos parámetros deben coincidir con los configurados en la máquina o en el
                                        sistema del casino.
                                    </Box>
                                    {this.renderGrupos(GRUPOS_SAS)}

                                    <Divider sx={{my: 3}} />

                                    <FormLabel>Configuración SMIB (Slot Machine Interface Board)</FormLabel>
                                    <Box sx={{fontSize: 13, color: 'text.secondary', mt: 1}}>
                                        Placa de interfaz SAS (ESP32 + Ethernet, RS-232 aislado). En el cajero se conecta a
                                        MoneyManager por USB, serial o Ethernet; en una máquina de juego reporta al servidor
                                        por Ethernet.
                                    </Box>
                                    {this.renderGrupos(GRUPOS_SMIB)}
                                </form>
                            </CardContent>
                            <CardActions>
                                <Button variant="contained" onClick={this.btnGuardarClick}>{this.state.modoEdicion === true ? "Modificar" : "Agregar"}</Button>
                                <Button variant="contained" onClick={this.prepararNuevo}>Cancelar</Button>
                            </CardActions>
                        </Card>
                    </Grid>
                </Grid>
                <br />
                <Card xs={8}>
                    <CardContent xs={8}>
                        <TableContainer xs={8}>
                            <Table stickyHeader class="table-data" xs={8}>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell style={{width: column.width}} key={column.field}>{column.headerName}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                {this.state.dispositivos.map((dispositivo) => (
                                    <Row item={dispositivo} eventSelect={this.btnSeleccionarClick} eventDelete={this.btnBorrarClick} />
                                ))}
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>
        )
    }
}

export default TabSas
